<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import AdminShell from "../components/AdminShell.vue";
import StatusBadge from "../components/StatusBadge.vue";
import { adminApi, type OrderDetailResponse } from "../lib/api";
import { useAdminSession } from "../lib/session";

const route = useRoute();
const session = useAdminSession();
const detail = ref<OrderDetailResponse | null>(null);
const loading = ref(true);
const error = ref("");
const retryOpen = ref(false);
const retryReason = ref("");
const retrying = ref(false);
const notice = ref("");

const orderId = computed(() => String(route.params.orderId));
const canRetry = computed(() => session.hasPermission("reports:retry") && detail.value?.report &&
  detail.value.report.status !== "ready");

async function load() {
  loading.value = true;
  error.value = "";
  try { detail.value = await adminApi.getOrder(orderId.value); }
  catch (caught) { error.value = caught instanceof Error ? caught.message : "無法讀取訂單。"; }
  finally { loading.value = false; }
}

async function retry() {
  if (!detail.value?.report || retryReason.value.trim().length < 5) return;
  retrying.value = true;
  error.value = "";
  try {
    const response = await adminApi.retryReport(detail.value.report.id, retryReason.value.trim());
    notice.value = response.deliveryPending
      ? "已受理並保留等待補送紀錄；目前自動修復排程尚未啟用，請持續追蹤此筆訂單。"
      : "報告已重新排入產生佇列。";
    retryOpen.value = false;
    retryReason.value = "";
    await load();
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "無法重新排程。";
  } finally { retrying.value = false; }
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-TW", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
  }).format(new Date(value));
}

function money(value: number) {
  return new Intl.NumberFormat("zh-TW", { style: "currency", currency: "TWD", maximumFractionDigits: 0 }).format(value);
}

const timeline = computed(() => {
  if (!detail.value) return [];
  const { order, report, actions } = detail.value;
  const items = [
    { time: order.createdAt, title: "訂單建立", detail: `${order.productName} · ${money(order.amount)}`, tone: "done" },
    { time: order.updatedAt, title: order.status === "paid" ? "款項確認完成" : "訂單狀態更新", detail: `目前狀態：${order.status}`, tone: order.status === "paid" ? "done" : "current" }
  ];
  if (report) {
    items.push({ time: report.createdAt, title: "報告任務建立", detail: `任務已建立，嘗試次數 ${report.attemptCount}`, tone: "done" });
    if (report.startedAt) items.push({ time: report.startedAt, title: "開始產生報告", detail: `階段 ${report.stage} · ${report.progress}%`, tone: report.status === "generating" ? "current" : "done" });
    if (report.status === "failed") items.push({ time: report.updatedAt, title: "報告產生失敗", detail: `${report.error?.code || "UNKNOWN"} · ${report.error?.failedStage || report.stage}`, tone: "danger" });
    if (report.completedAt) items.push({ time: report.completedAt, title: "PDF 交付完成", detail: "報告已可供下載", tone: "done" });
  }
  for (const action of actions) items.push({ time: action.createdAt, title: "營運人員重新排程", detail: `${action.actorEmail}：${action.reason}`, tone: action.status === "delivery_pending" ? "danger" : "current" });
  return items.sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
});

onMounted(load);
</script>

<template>
  <AdminShell>
    <header class="detail-head">
      <div>
        <RouterLink class="back-link" to="/orders">← 返回訂單</RouterLink>
        <p class="eyebrow">Order trace</p>
        <h1>訂單流向</h1>
        <code>{{ orderId }}</code>
      </div>
      <div v-if="detail" class="head-actions">
        <a v-if="detail.downloads" class="secondary-button" :href="detail.downloads.pdfUrl" target="_blank" rel="noopener">下載 PDF</a>
        <button v-if="canRetry" class="danger-button" type="button" @click="retryOpen = true">重新排程</button>
      </div>
    </header>

    <div v-if="notice" class="success-notice">{{ notice }}</div>
    <div v-if="error" class="inline-error" role="alert"><span>{{ error }}</span><button type="button" @click="load">重新讀取</button></div>
    <div v-if="loading" class="loading-state">正在重建這筆訂單的流向…</div>

    <template v-else-if="detail">
      <section class="detail-grid">
        <article class="summary-card">
          <p class="card-kicker">款項</p>
          <div class="card-title-row"><h2>{{ money(detail.order.amount) }}</h2><StatusBadge :status="detail.order.status" /></div>
          <dl>
            <div><dt>付款編號</dt><dd><code>{{ detail.order.paymentTradeNo }}</code></dd></div>
            <div><dt>交易編號</dt><dd><code>{{ detail.order.paymentProviderTransactionId || "尚無" }}</code></dd></div>
            <div><dt>用戶 UID</dt><dd><code>{{ detail.order.ownerId }}</code></dd></div>
            <div><dt>最後更新</dt><dd>{{ formatDate(detail.order.updatedAt) }}</dd></div>
          </dl>
        </article>

        <article class="summary-card report-card">
          <p class="card-kicker">PDF 報告</p>
          <template v-if="detail.report">
            <div class="card-title-row"><h2>{{ detail.report.progress }}<small>%</small></h2><StatusBadge :status="detail.report.status" /></div>
            <div class="progress-track"><i :style="{ width: `${detail.report.progress}%` }"></i></div>
            <dl>
              <div><dt>目前階段</dt><dd>{{ detail.report.stage }}</dd></div>
              <div><dt>嘗試次數</dt><dd>{{ detail.report.attemptCount }} / 3</dd></div>
              <div><dt>任務已排入</dt><dd>{{ detail.report.taskEnqueued ? "是" : "否" }}</dd></div>
              <div v-if="detail.report.error"><dt>失敗代碼</dt><dd><code class="danger-text">{{ detail.report.error.code }}</code></dd></div>
            </dl>
          </template>
          <div v-else class="no-report"><strong>尚未建立報告</strong><p>只有確認付款後，系統才會建立報告任務。</p></div>
        </article>
      </section>

      <section class="trace-section">
        <header><div><p class="eyebrow">Immutable trail</p><h2>處理脈絡</h2></div><p>由早至晚</p></header>
        <ol class="timeline-list">
          <li v-for="item in timeline" :key="`${item.time}-${item.title}`" :data-tone="item.tone">
            <time>{{ formatDate(item.time) }}</time><i aria-hidden="true"></i>
            <div><strong>{{ item.title }}</strong><p>{{ item.detail }}</p></div>
          </li>
        </ol>
      </section>

      <section v-if="detail.actions.length" class="audit-section">
        <header><h2>操作稽核</h2><span>{{ detail.actions.length }} 筆</span></header>
        <article v-for="action in detail.actions" :key="action.id" class="audit-row">
          <StatusBadge :status="action.status" />
          <div><strong>{{ action.actorEmail }}</strong><p>{{ action.reason }}</p></div>
          <time>{{ formatDate(action.createdAt) }}</time>
        </article>
      </section>
    </template>

    <div v-if="retryOpen" class="dialog-backdrop" @click.self="retryOpen = false">
      <form class="action-dialog" @submit.prevent="retry">
        <p class="eyebrow danger-text">Controlled action</p>
        <h2>重新排程 PDF 報告</h2>
        <p>這會將目前任務退回等待佇列。系統會保存操作人員、前後狀態與原因。</p>
        <label>處理原因<textarea v-model="retryReason" rows="4" minlength="5" maxlength="500" placeholder="例如：報告產生逾時，確認款項與資料正常後重新排程" required></textarea></label>
        <div class="dialog-actions">
          <button class="text-button" type="button" @click="retryOpen = false">取消</button>
          <button class="danger-button" type="submit" :disabled="retrying || retryReason.trim().length < 5">{{ retrying ? "排程中…" : "確認重新排程" }}</button>
        </div>
      </form>
    </div>
  </AdminShell>
</template>
